import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';

import { AuthService } from '@core/service/auth.service';
import { ToastService } from '@core/service/toast.service';
import { FormErrorComponent } from '@shared/components/form-error/form-error.component';

type ChangePasswordFormShape = {
  oldPassword: FormControl<string>;
  newPassword: FormControl<string>;
  confirmPassword: FormControl<string>;
};

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const a = group.get('newPassword')?.value;
  const b = group.get('confirmPassword')?.value;
  return a && b && a !== b ? { passwordsMismatch: true } : null;
}

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    PasswordModule,
    RippleModule,
    ToastModule,
    FormErrorComponent,
  ],
  templateUrl: './change-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly submitting = signal(false);
  changeForm!: FormGroup<ChangePasswordFormShape>;

  ngOnInit(): void {
    this.changeForm = this.fb.nonNullable.group<ChangePasswordFormShape>(
      {
        oldPassword: this.fb.nonNullable.control('', Validators.required),
        newPassword: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(8)]),
        confirmPassword: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(8)]),
      },
      { validators: passwordsMatch },
    );
  }

  submit(): void {
    if (this.changeForm.invalid) {
      this.changeForm.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    const { oldPassword, newPassword } = this.changeForm.getRawValue();
    this.authService
      .changePassword({ oldPassword, newPassword })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          this.toast.showSuccess('Password changed', res?.message ?? 'Your password has been updated.');
          this.changeForm.reset();
          this.router.navigate(['/profile']);
        },
        error: () => this.submitting.set(false),
      });
  }
}
